import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { useShiftStore } from '@/stores/shift';
import { useActivePatients, useOrientedPatients } from '@/stores/patients';
import {
  clearCurrentShift,
  finalizeCurrentShiftForSubmission,
  listenForReportUrl,
  updateCurrentShiftFields,
} from '@/services/firestore/shifts';
import { submitShift, type StudentData } from '@/services/api/submit-shift';
import {
  combineDateAndTime,
  formatHm,
  formatYmd,
  roundToDisplayedHalfHour,
} from '@/lib/date';
import { PillButton, PillInput, PillSelect } from '@/components/FormField';
import { DateField } from '@/components/DateField';
import { BackHeader } from '@/components/BackHeader';

const PLACES = [
  { value: 'chum', label: 'CHUM' },
  { value: 'hscm', label: 'HSCM' },
  { value: 'hmr', label: 'HMR' },
  { value: 'hjt', label: 'HJT' },
  { value: 'verdun', label: 'Verdun' },
  { value: 'hnd', label: 'HND' },
  { value: 'autre', label: 'Autre' },
];

const BILLING_TYPES = [
  { value: 'mixte', label: 'Mixte' },
  { value: 'acte', label: 'Acte' },
];

export function CurrentShiftPage() {
  const profile = useAuthStore((s) => s.profile);
  const shift = useShiftStore((s) => s.currentShift);
  const cachedShiftTotal = useShiftStore((s) => s.cachedShiftTotal);
  const verifyCurrentShift = useShiftStore((s) => s.verifyCurrentShift);
  const startNewShift = useShiftStore((s) => s.startNewShift);
  const resetShift = useShiftStore((s) => s.resetShift);
  const refreshActive = useActivePatients((s) => s.refresh);
  const refreshOriented = useOrientedPatients((s) => s.refresh);
  const navigate = useNavigate();

  const [dateStart, setDateStart] = useState('');
  const [timeStart, setTimeStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [timeEnd, setTimeEnd] = useState('');
  const [place, setPlace] = useState('chum');
  const [billingType, setBillingType] = useState('mixte');

  const [students, setStudents] = useState(
    Array.from({ length: 3 }, () => ({ name: '', time: '' })),
  );

  function updateStudent(i: number, field: 'name' | 'time', value: string) {
    setStudents((prev) =>
      prev.map((s, idx) => (idx === i ? { ...s, [field]: value } : s)),
    );
  }

  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!profile) return;
    void verifyCurrentShift(profile.uid);
  }, [profile, verifyCurrentShift]);

  useEffect(() => {
    if (!shift.dateStarted) return;
    const start = roundToDisplayedHalfHour(new Date(shift.dateStarted));
    setDateStart(formatYmd(start));
    setTimeStart(formatHm(start));
    if (shift.dateEnded) {
      const end = roundToDisplayedHalfHour(new Date(shift.dateEnded));
      setDateEnd(formatYmd(end));
      setTimeEnd(formatHm(end));
    }
    if (shift.place) setPlace(shift.place);
    if (shift.facturationType) setBillingType(shift.facturationType);
  }, [shift]);

  if (!profile) return null;

  const hasShift = !!shift.dateStarted;

  async function onStart() {
    if (!profile) return;
    setError(null);
    try {
      await startNewShift({
        uid: profile.uid,
        mainPlace: profile.mainPlace || 'chum',
        mdType: profile.mdType || 'cfpc',
        facturationType: profile.billingType || 'mixte',
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function finalizeAndSubmit() {
    if (!profile) return;
    setError(null);
    setLoadingMessage('Préparation du quart…');
    try {
      const start = combineDateAndTime(dateStart, timeStart);
      const end = combineDateAndTime(dateEnd, timeEnd);
      if (end.getTime() <= start.getTime()) {
        throw new Error('La fin doit être après le début.');
      }

      await updateCurrentShiftFields(profile.uid, {
        place,
        facturationType: billingType,
      });

      const finalShiftId = await finalizeCurrentShiftForSubmission({
        uid: profile.uid,
        start,
        end,
      });

      const studentData: StudentData = {};
      students.forEach(({ name, time }, i) => {
        const n = name.trim();
        const t = time.trim();
        if (n) studentData[`student_${i + 1}` as keyof StudentData] = n;
        if (t) studentData[`student_${i + 1}_time` as keyof StudentData] = t;
      });

      setLoadingMessage('Soumission au serveur…');
      const res = await submitShift({
        userId: profile.uid,
        shiftId: finalShiftId,
        studentData,
      });
      if (res.response !== 'success') {
        throw new Error('La soumission a échoué côté serveur.');
      }

      setLoadingMessage('Génération du rapport… (jusqu’à 2 min)');
      const { promise } = listenForReportUrl({
        uid: profile.uid,
        shiftId: finalShiftId,
        timeoutMs: 120_000,
      });
      try {
        await promise;
      } catch {
        // report still rendering — we'll show it later from history
      }

      await clearCurrentShift(profile.uid);
      resetShift();
      await Promise.all([
        refreshActive(profile.uid),
        refreshOriented(profile.uid),
      ]);
      navigate('/history', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingMessage(null);
    }
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!profile) return;
    try {
      const start = combineDateAndTime(dateStart, timeStart);
      const end = combineDateAndTime(dateEnd, timeEnd);
      if (end.getTime() <= start.getTime()) {
        setError('La fin doit être après le début.');
        return;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return;
    }
    setError(null);
    await finalizeAndSubmit();
  }

  return (
    <div className="p-4 flex flex-col gap-4 pb-24">
      <BackHeader title="Soumettre le quart" />

      {!hasShift ? (
        <div className="bg-brand-dark/40 rounded-2xl p-4 shadow-card flex flex-col gap-3">
          <p className="text-sm text-white/70">Aucun quart en cours.</p>
          {error && <p className="text-sm text-red-300">{error}</p>}
          <PillButton onClick={() => void onStart()}>
            Démarrer un quart
          </PillButton>
        </div>
      ) : (
        <form
          onSubmit={onSubmit}
          className="bg-brand-dark/40 rounded-2xl p-4 shadow-card flex flex-col gap-3"
        >
          <div className="flex items-center justify-between">
            <span className="text-sm text-white/70">Total facturé</span>
            <span className="text-xl font-semibold">{cachedShiftTotal} $</span>
          </div>

          <label className="text-xs text-white/70 mt-2">Début</label>
          <div className="flex gap-2">
            <DateField value={dateStart} onChange={setDateStart} required />
            <DateField
              variant="time"
              value={timeStart}
              onChange={setTimeStart}
              required
            />
          </div>

          <label className="text-xs text-white/70 mt-2">Fin</label>
          <div className="flex gap-2">
            <DateField value={dateEnd} onChange={setDateEnd} required />
            <DateField
              variant="time"
              value={timeEnd}
              onChange={setTimeEnd}
              required
            />
          </div>

          <label className="text-xs text-white/70 mt-2">Lieu de travail</label>
          <PillSelect
            value={place}
            onChange={(e) => setPlace(e.target.value)}
            options={PLACES}
          />

          <label className="text-xs text-white/70 mt-2">
            Type de rémunération
          </label>
          <PillSelect
            value={billingType}
            onChange={(e) => setBillingType(e.target.value)}
            options={BILLING_TYPES}
          />

          <details className="mt-2">
            <summary className="text-sm text-white/80 cursor-pointer">
              Étudiants (optionnel)
            </summary>
            <div className="flex flex-col gap-2 mt-2">
              {students.map((s, i) => (
                <div key={i} className="flex gap-2">
                  <PillInput
                    placeholder="Code (ex. R19929)"
                    autoCapitalize="characters"
                    value={s.name}
                    onChange={(e) =>
                      updateStudent(i, 'name', e.target.value.toUpperCase())
                    }
                  />
                  <PillInput
                    type="number"
                    inputMode="numeric"
                    placeholder="Heures (4 ou 8)"
                    value={s.time}
                    onChange={(e) => updateStudent(i, 'time', e.target.value)}
                  />
                </div>
              ))}
            </div>
          </details>

          {error && <p className="text-sm text-red-300">{error}</p>}
          {loadingMessage && (
            <p className="text-sm text-white/80">{loadingMessage}</p>
          )}

          <PillButton type="submit" disabled={loadingMessage != null}>
            {loadingMessage ? 'Soumission…' : 'Soumettre le quart'}
          </PillButton>
        </form>
      )}
    </div>
  );
}
